"use client";

import React, { useMemo, useState } from "react";

import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { getWeekRange } from "@/utils/util";
import { TIMING_YEAR_START, TIMING_YEAR_END } from "@/utils/dateFormat";
import { getTaskBadge } from "@/utils/taskBadge";

const CALENDAR_START_MONTH = new Date(TIMING_YEAR_START, 0, 1);
const CALENDAR_END_MONTH = new Date(TIMING_YEAR_END, 11, 31);

/** The selection for one day (00:00 - 23:59). */
function daySelection(date) {
  const from = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const to = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  return { kind: "day", from, to };
}

/** The selection for the week (Monday - Sunday) of a date. */
function weekSelection(date) {
  return { kind: "week", ...getWeekRange(date) };
}

// A day's dot shows its most pressing task, in that status's colour (utils/taskStatus.js)
const URGENCY = ["overdue", "soon", "week", "later"];
const DOT = "after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:size-1.5 after:rounded-full";
const DOT_CLASSES = {
  overdue: `${DOT} after:bg-red-500`,
  soon: `${DOT} after:bg-orange-500`,
  week: `${DOT} after:bg-yellow-500`,
  later: `${DOT} after:bg-blue-500`,
};
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/**
 * The dashboard's calendar: a dot on each day with tasks (in the colour of its most pressing one),
 * and a date filter for the tasks. Controlled: `selection` is null (no date filter - all tasks) or
 * { kind: 'day' | 'week', from, to }; clicking a day selects it, "Whole week" widens a day to its
 * week, and "Reset" clears it and goes back to this month. Browsed to another month without a filter,
 * "Today" goes back to this month.
 */
export function TaskCalendar({ trials, selection, onSelectionChange }) {
  const thisMonth = () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1); };
  const [month, setMonth] = useState(thisMonth);
  const inThisMonth = month.getFullYear() === new Date().getFullYear() && month.getMonth() === new Date().getMonth();
  const reset = () => {
    onSelectionChange(null);
    setMonth(thisMonth());
  };

  // Each day's most pressing status
  const urgencyByDay = useMemo(() => {
    const byDay = new Map();
    for (const row of trials) {
      if (!row.task?.dueDate) continue;
      const status = getTaskBadge(row.task).status;
      if (!URGENCY.includes(status)) continue;
      const key = dayKey(new Date(row.task.dueDate));
      const current = byDay.get(key);
      if (!current || URGENCY.indexOf(status) < URGENCY.indexOf(current)) byDay.set(key, status);
    }
    return byDay;
  }, [trials]);

  const inSelection = (date) => !!selection && date >= selection.from && date <= selection.to;
  const modifiers = {
    ...Object.fromEntries(URGENCY.map((status) => [`due_${status}`, (date) => urgencyByDay.get(dayKey(date)) === status])),
    inWeek: (date) => selection?.kind === "week" && inSelection(date),
    weekStart: (date) => selection?.kind === "week" && inSelection(date) && date.getDay() === 1,
    weekEnd: (date) => selection?.kind === "week" && inSelection(date) && date.getDay() === 0,
    chosenDay: (date) => selection?.kind === "day" && inSelection(date),
  };
  const modifiersClassNames = {
    ...Object.fromEntries(URGENCY.map((status) => [`due_${status}`, DOT_CLASSES[status]])),
    inWeek: "bg-emerald-50 dark:bg-emerald-950/40",
    weekStart: "rounded-l-md",
    weekEnd: "rounded-r-md",
    chosenDay: "rounded-md ring-2 ring-inset ring-emerald-500",
  };

  return (
    <>
      <Calendar
        month={month}
        onMonthChange={setMonth}
        onDayClick={(date) => onSelectionChange(daySelection(date))}
        className="rounded-md border shadow-sm"
        classNames={{ today: "font-bold text-emerald-700 dark:text-emerald-400" }}
        captionLayout="dropdown"
        startMonth={CALENDAR_START_MONTH}
        endMonth={CALENDAR_END_MONTH}
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        weekStartsOn={1} // 0 = Sunday, 1 = Monday
      />
      <div className="mt-1 ml-1 flex gap-1">
        {selection?.kind === "day" && (
          <Button size="sm" variant="outline" className="cursor-pointer" onClick={() => onSelectionChange(weekSelection(selection.from))}>
            Whole week
          </Button>
        )}
        {selection && (
          <Button size="sm" className="cursor-pointer" onClick={reset}>
            Reset
          </Button>
        )}
        {!selection && !inThisMonth && (
          <Button size="sm" variant="outline" className="cursor-pointer" onClick={() => setMonth(thisMonth())}>
            Today
          </Button>
        )}
      </div>
    </>
  );
}
